import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react"
import { STORAGE_KEYS, readStored, writeStored } from "@/lib/storage"

export type ThemeChoice = "light" | "dark" | "system"
export type ResolvedTheme = "light" | "dark"

/** `devsms.theme` (LLD §4.4); index.html's no-flash script reads the same key. */
export const THEME_STORAGE_KEY = STORAGE_KEYS.theme

interface ThemeContextValue {
  /** What the user chose, including "system". */
  choice: ThemeChoice
  /** What that resolves to right now. */
  resolved: ResolvedTheme
  setChoice: (choice: ThemeChoice) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const DARK_QUERY = "(prefers-color-scheme: dark)"

/**
 * The OS preference is external state that changes on its own, which is exactly
 * what useSyncExternalStore is for. Subscribing this way rather than with
 * useEffect + setState avoids a cascading render on every theme change.
 */
function subscribeToSystemTheme(onChange: () => void): () => void {
  const media = window.matchMedia(DARK_QUERY)
  media.addEventListener("change", onChange)
  return () => {
    media.removeEventListener("change", onChange)
  }
}

const getSystemIsDark = (): boolean => window.matchMedia(DARK_QUERY).matches

/** Blocked storage reads as `null`, so a theme failing to load never stops the app rendering. */
export function readStoredChoice(): ThemeChoice {
  const raw = readStored(THEME_STORAGE_KEY)
  return raw === "light" || raw === "dark" || raw === "system" ? raw : "system"
}

export function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement
  root.dataset.theme = resolved
  root.style.colorScheme = resolved
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(readStoredChoice)

  const systemIsDark = useSyncExternalStore(
    subscribeToSystemTheme,
    getSystemIsDark,
    () => false, // server / no-DOM fallback
  )

  // Derived during render, not stored. An explicit choice always beats the OS.
  const resolved: ResolvedTheme = choice === "system" ? (systemIsDark ? "dark" : "light") : choice

  // The only effect: reflect the resolved theme onto <html>. No setState here.
  useEffect(() => {
    applyTheme(resolved)
  }, [resolved])

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next)
    writeStored(THEME_STORAGE_KEY, next)
  }, [])

  const value = useMemo<ThemeContextValue>(
    () => ({ choice, resolved, setChoice }),
    [choice, resolved, setChoice],
  )

  return <ThemeContext value={value}>{children}</ThemeContext>
}

export function useTheme(): ThemeContextValue {
  const ctx = use(ThemeContext)
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>")
  return ctx
}
